/**
 * FACT-CHECK NON-CLAIMS — sentences that carry a citation or a number and still assert nothing about the law.
 *
 * The 27-article ITC run of 2026-10-05 left 20 claims `unsupported` or `contradicted`; about 14 of them were
 * not claims at all, so the judge was asked whether a statute "states" a table header, a checklist item, a
 * question, a pointer to a row, a piece of advice or an editor's adjective, and honestly answered no:
 *
 *   header      "What the audit found ; Ground under § 41.41(a) ; Burden rule in § 41.43"
 *   fragment    "The burden rule that applies under § 41.43."   "The Form 01-929 exemption certificate."
 *   question    "Which of the grounds in § 41.41(a) will the memo address?"
 *   pointer     "The fourth row rests on § 1.111(f)."   "Section 23.014 is the section to read against ..."
 *   advice      "Check what the model omits: a rendition on file, ... § 23.01(e) changes the answer."
 *   judgement   "Section 23.012 is specific about the inputs."
 *
 * A false `unsupported` on a sentence like these is noise a person has to clear, and it lets a real
 * `unsupported` hide among it. They are skipped here, in code, before the judge ever sees them.
 *
 * The line is drawn so that a rule stated without a verb of its own stays a rule when it carries a date, a
 * figure or a modal duty ("Protest deadline: May 15."; "Calendar the dates: April 15 for the rendition, May 15
 * on written request."), and a sentence that does state a duty is never skipped for its grammar.
 *
 * Pure: no I/O, no model, no clock.
 */

import { isQuestion, withoutQuotes } from './article-factcheck-classify';

export type NonClaimReason = 'question' | 'fragment' | 'advice' | 'pointer' | 'judgement';

/** A citation as the article writes it: its own numbers say nothing about the sentence's grammar. */
const CITATION =
  /(?:§§?|\b(?:Sections?|Secs?\.|Chapters?|Subsections?|Subchapters?))\s*\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)*(?:\s*\([A-Za-z0-9-]{1,4}\))*/gi;

/** A duty in the sentence's own words. */
const MODAL_DUTY = /\b(?:must|shall|required?|requires?|may not|entitled|obligat\w+)\b/i;

const MONTH =
  '(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?';
/** A date or a day count: a deadline stated, whatever the grammar. */
const DATE_OR_PERIOD = new RegExp(
  `\\b${MONTH}\\s+\\d{1,2}\\b|\\b\\d{1,3}[\\s-](?:calendar\\s+|business\\s+|working\\s+)?(?:days?|weeks?|months?|years?)\\b`,
  'i',
);
const FIGURE = /\$\s?\d|\d\s?(?:%|percent\b)|\b(?:at least|no more than|up to)\s+\$?\d/i;

/** Words that make a verb a verb, in every person and tense a sentence of tax prose uses. */
const AUXILIARY = new Set([
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'am',
  'has',
  'have',
  'had',
  'having',
  'do',
  'does',
  'did',
  'can',
  'could',
  'may',
  'might',
  'must',
  'shall',
  'should',
  'will',
  'would',
  'cannot',
  "isn't",
  "aren't",
  "doesn't",
  "don't",
  "can't",
  "won't",
]);

/**
 * Verbs a claim about a statute turns on. Third person and base form are listed; `-ed` and `-ing`
 * words are read as verbs by their ending (a participle in a noun phrase is rare enough, and the cost of
 * reading one as a verb is only that a fragment stays a claim).
 */
const VERB_STEMS = [
  'abate',
  'accept',
  'accrue',
  'add',
  'address',
  'adjust',
  'allow',
  'amend',
  'appear',
  'apply',
  'appraise',
  'ask',
  'assess',
  'assign',
  'attach',
  'avoid',
  'bar',
  'base',
  'become',
  'begin',
  'bind',
  'bring',
  'build',
  'calculate',
  'call',
  'carry',
  'cause',
  'change',
  'charge',
  'check',
  'choose',
  'claim',
  'classify',
  'close',
  'collect',
  'come',
  'compare',
  'complete',
  'comply',
  'consider',
  'consist',
  'contain',
  'continue',
  'control',
  'cost',
  'count',
  'cover',
  'create',
  'decide',
  'define',
  'deliver',
  'demand',
  'depend',
  'describe',
  'determine',
  'differ',
  'direct',
  'disclose',
  'drop',
  'earn',
  'elect',
  'enter',
  'equal',
  'establish',
  'exceed',
  'exclude',
  'exempt',
  'exist',
  'expire',
  'extend',
  'fail',
  'fall',
  'file',
  'find',
  'fix',
  'follow',
  'forfeit',
  'fund',
  'get',
  'give',
  'govern',
  'grant',
  'happen',
  'hold',
  'identify',
  'impose',
  'include',
  'increase',
  'inform',
  'issue',
  'keep',
  'know',
  'lapse',
  'leave',
  'let',
  'lie',
  'limit',
  'list',
  'lose',
  'make',
  'mean',
  'meet',
  'mention',
  'miss',
  'name',
  'need',
  'notify',
  'obtain',
  'occur',
  'offer',
  'omit',
  'open',
  'owe',
  'own',
  'pass',
  'pay',
  'permit',
  'place',
  'prevent',
  'prescribe',
  'present',
  'prevail',
  'prohibit',
  'protect',
  'prove',
  'provide',
  'publish',
  'qualify',
  'raise',
  'reach',
  'read',
  'receive',
  'reduce',
  'refer',
  'reflect',
  'refund',
  'regard',
  'relate',
  'release',
  'rely',
  'remain',
  'remove',
  'render',
  'repeal',
  'replace',
  'report',
  'request',
  'require',
  'resolve',
  'rest',
  'restrict',
  'result',
  'retain',
  'return',
  'reverse',
  'review',
  'rule',
  'run',
  'say',
  'see',
  'seek',
  'send',
  'serve',
  'set',
  'settle',
  'shift',
  'show',
  'sit',
  'specify',
  'start',
  'state',
  'stay',
  'stop',
  'submit',
  'suffice',
  'supersede',
  'support',
  'take',
  'tax',
  'tell',
  'test',
  'toll',
  'treat',
  'trigger',
  'turn',
  'underlie',
  'understate',
  'use',
  'value',
  'vary',
  'verify',
  'waive',
  'want',
  'win',
  'work',
  'write',
  'yield',
  'argue',
  'assert',
  'cancel',
  'clarify',
  'commence',
  'conclude',
  'confirm',
  'constitute',
  'contend',
  'deny',
  'detail',
  'dismiss',
  'end',
  'ensure',
  'entail',
  'explain',
  'indicate',
  'involve',
  'lay',
  'levy',
  'note',
  'object',
  'outline',
  'point',
  'represent',
  'reserve',
  'revoke',
  'sell',
  'spell',
  'suggest',
  'terminate',
];

const thirdPerson = (stem: string): string =>
  /(?:s|x|z|ch|sh)$/.test(stem)
    ? `${stem}es`
    : /[^aeiou]y$/.test(stem)
      ? `${stem.slice(0, -1)}ies`
      : `${stem}s`;

/**
 * Verbs whose -s form is also a plural noun in tax prose ("the rules", "its limits", "both requests"). They count
 * as a verb only when the word before them is not one that introduces a noun, so "The statute sets the rate" has a
 * verb and "The rules for ..." does not. Base forms are never counted on their own: "the rule", "a request" and
 * "to state" are not finite verbs, and a base form after a modal is found through the modal.
 */
const AMBIGUOUS_STEMS = new Set([
  'answer',
  'appeal',
  'bar',
  'base',
  'cause',
  'change',
  'charge',
  'claim',
  'control',
  'cost',
  'count',
  'cover',
  'detail',
  'direct',
  'document',
  'drop',
  'end',
  'figure',
  'file',
  'fund',
  'increase',
  'issue',
  'limit',
  'list',
  'mark',
  'name',
  'note',
  'number',
  'object',
  'offer',
  'order',
  'pass',
  'permit',
  'place',
  'plan',
  'point',
  'present',
  'process',
  'protest',
  'record',
  'release',
  'render',
  'report',
  'request',
  'rest',
  'result',
  'return',
  'review',
  'rule',
  'run',
  'set',
  'show',
  'state',
  'stay',
  'stop',
  'support',
  'tax',
  'test',
  'trigger',
  'turn',
  'use',
  'value',
  'work',
]);
const UNAMBIGUOUS_THIRD = new Set(
  VERB_STEMS.filter((stem) => !AMBIGUOUS_STEMS.has(stem)).map(thirdPerson),
);
const AMBIGUOUS_THIRD = new Set([...AMBIGUOUS_STEMS].map(thirdPerson));
/** Base forms that are a finite verb after a plural subject ("The minimums depend on the county"). Never after "to". */
const PLURAL_SUBJECT_VERBS = new Set([
  'accrue',
  'add',
  'agree',
  'appear',
  'apply',
  'arise',
  'attach',
  'become',
  'begin',
  'depend',
  'differ',
  'exceed',
  'exist',
  'expire',
  'fail',
  'fall',
  'follow',
  'give',
  'govern',
  'hold',
  'include',
  'lapse',
  'lie',
  'lose',
  'make',
  'mean',
  'need',
  'occur',
  'owe',
  'pay',
  'prevail',
  'provide',
  'qualify',
  'reach',
  'refer',
  'relate',
  'rely',
  'remain',
  'require',
  'sit',
  'start',
  'suffice',
  'take',
  'vary',
  'want',
  'allow',
  'let',
  'keep',
  'get',
]);
/** A word that introduces a noun phrase or joins two: what follows it is a noun, not a verb. */
const NOUN_INTRODUCER = new Set([
  'the',
  'a',
  'an',
  'any',
  'each',
  'every',
  'all',
  'both',
  'no',
  'some',
  'these',
  'those',
  'such',
  'its',
  'their',
  'your',
  'our',
  'this',
  'that',
  'of',
  'for',
  'in',
  'on',
  'under',
  'by',
  'with',
  'from',
  'to',
  'and',
  'or',
  'nor',
  'than',
  'other',
  'more',
  'new',
  'same',
  'certain',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'many',
  'several',
  'few',
  'between',
  'about',
  'per',
  'at',
  'as',
  'into',
  'over',
]);
/** Irregular past forms and the participles that do not end in -ed. */
const IRREGULAR_PAST = new Set([
  'bound',
  'broke',
  'brought',
  'built',
  'came',
  'chose',
  'fell',
  'found',
  'gave',
  'got',
  'grew',
  'held',
  'kept',
  'knew',
  'left',
  'lost',
  'made',
  'met',
  'paid',
  'ran',
  'said',
  'sent',
  'shown',
  'sold',
  'stood',
  'taken',
  'took',
  'told',
  'won',
  'wrote',
  'written',
]);
/**
 * A word the list does not know, ending in -s, between a noun and the opening of its object ("The ARB hears
 * the protest") is read as a verb. The list cannot hold every verb of tax procedure, and a sentence wrongly
 * kept as a claim costs a judge call where one wrongly skipped is a rule never checked.
 */
const VERB_OBJECT_OPENER = new Set([
  'the',
  'a',
  'an',
  'that',
  'its',
  'their',
  'this',
  'these',
  'those',
  'any',
  'each',
  'all',
  'no',
  'under',
  'in',
  'on',
  'at',
  'to',
  'for',
  'from',
  'by',
  'with',
  'only',
  'also',
  'not',
  'both',
  'it',
  'them',
]);
const NOT_A_VERB_ENDING = new Set(['need', 'speed', 'deed', 'seed', 'feed', 'indeed', 'hundred']);

function hasFiniteVerbIn(text: string): boolean {
  const words = text
    .toLowerCase()
    .split(/[^a-z']+/)
    .map((raw) => raw.replace(/^'+|'+$/g, ''))
    .filter(Boolean);
  for (const [index, word] of words.entries()) {
    if (AUXILIARY.has(word) || UNAMBIGUOUS_THIRD.has(word) || IRREGULAR_PAST.has(word)) return true;
    if (word.length > 4 && /(?:ed|ing)$/.test(word) && !NOT_A_VERB_ENDING.has(word)) return true;
    if (AMBIGUOUS_THIRD.has(word) && index > 0 && !NOUN_INTRODUCER.has(words[index - 1]!))
      return true;
    if (PLURAL_SUBJECT_VERBS.has(word) && index > 0 && words[index - 1] !== 'to') return true;
    if (
      index > 0 &&
      word.length >= 5 &&
      /[^s]s$/.test(word) &&
      !/(?:us|is|ics)$/.test(word) &&
      !NOUN_INTRODUCER.has(words[index - 1]!) &&
      VERB_OBJECT_OPENER.has(words[index + 1] ?? '')
    )
      return true;
  }
  return false;
}

/** What a sentence says once its quotations, parentheticals and citations are out of the way. */
function body(sentence: string): string {
  return withoutQuotes(sentence)
    .replace(CITATION, ' ref ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Does the sentence have a verb of its own? A relative clause is not its main verb: "The burden rule that
 * applies under § 41.43." is a noun phrase with a clause hung on it, so only the words before the relative
 * pronoun count. A sentence that opens with the pronoun or a subordinator ("When the owner fails ...",
 * "Whether equipment ... depends on the facts") is read whole.
 */
export function hasFiniteVerb(sentence: string): boolean {
  const text = body(sentence);
  // Past about this length the main verb is as likely to come AFTER the relative clause ("A protest that is
  // not settled first ends in a hearing"), so the sentence is read whole.
  const relative =
    wordCount(text) <= RELATIVE_CLAUSE_MAX_WORDS
      ? /\s(?:that|which|who|whom|whose)\s/i.exec(text)
      : null;
  const main = relative && relative.index > 0 ? text.slice(0, relative.index) : text;
  return hasFiniteVerbIn(main);
}

const wordCount = (text: string) => text.split(/\s+/).filter(Boolean).length;
const RELATIVE_CLAUSE_MAX_WORDS = 14;
/** A fragment is short and opens like a noun phrase; a long sentence with an unfamiliar verb is not one. */
const FRAGMENT_MAX_WORDS = 18;
/**
 * A fragment only matters when it carries a citation or a form number: that is what would make it a claim to
 * judge. A noun phrase with neither is not extracted anyway, and the verb list below is too short to be trusted
 * on prose that asserts nothing the check reads.
 */
const NAMES_A_PROVISION_OR_FORM = /§|\b(?:Sections?|Secs?\.|Chapters?|Subchapters?|Forms?)\s+\d/i;
const NOUN_PHRASE_OPENER =
  /^(?:the|a|an|any|each|every|all|both|one|two|three|four|five|six|your|our|its|their|this|these|those|no|some|another|such)\b/i;

const IMPERATIVE_VERBS = new Set([
  'add',
  'ask',
  'attach',
  'avoid',
  'bring',
  'build',
  'calendar',
  'call',
  'calculate',
  'check',
  'collect',
  'compare',
  'confirm',
  'consider',
  'contact',
  'decide',
  'deliver',
  'document',
  'ensure',
  'estimate',
  'file',
  'find',
  'gather',
  'get',
  'identify',
  'keep',
  'list',
  'make',
  'map',
  'model',
  'note',
  'plan',
  'prepare',
  'pull',
  'put',
  'read',
  'record',
  'remember',
  'request',
  'review',
  'schedule',
  'see',
  'send',
  'set',
  'start',
  'take',
  'test',
  'track',
  'treat',
  'use',
  'verify',
  'watch',
]);

/**
 * An instruction to the reader: a sentence that opens with a base-form verb ("Check what the model omits",
 * "Deliver renditions ... on time, and consider whether ...") or with "do not". Advice is not a statement of
 * what a statute says. It stays a claim when it also states a legal duty in its own words ("must", "shall"),
 * a date or day count, or a figure: "Calendar the dates: April 15 for the rendition, May 15 on written
 * request." is advice that carries the rendition deadline.
 */
const CITATION_THEN_STATUTE_VERB = new RegExp(
  `(?:§§?|\\b(?:Sections?|Secs?\\.))\\s*\\d[\\w.]*(?:\\s*\\([A-Za-z0-9-]{1,4}\\))*\\s+(?:lets?|allows?|permits?|requires?|provides?|states?|says?|sets?|turns?\\s+on|governs?|gives?|bars?|limits?|defines?|treats?|imposes?|caps?|exempts?)\\b`,
  'i',
);

export function isAdvice(sentence: string): boolean {
  const text = body(sentence).replace(/^(?:then|also|first|next|finally|and|but|so)[,\s]+/i, '');
  const first = /^([A-Za-z']+)/.exec(text)?.[1]?.toLowerCase();
  if (!first) return false;
  const imperative =
    IMPERATIVE_VERBS.has(first) ||
    (/^(?:do|don't|never|always)$/.test(first) && /^(?:do not|don't|never|always)\b/i.test(text));
  if (!imperative) return false;
  const own = withoutQuotes(sentence);
  return !(
    MODAL_DUTY.test(own) ||
    DATE_OR_PERIOD.test(own) ||
    FIGURE.test(own) ||
    CITATION_THEN_STATUTE_VERB.test(own)
  );
}

/**
 * A reference to the article's own parts or to where the law lives, rather than a statement of it:
 * "The fourth row rests on § 1.111(f).", "Two more rules in § 1.111 sit around the checklist.",
 * "Section 23.014 is the section to read against an income-based value ...". It is a pointer only when the
 * sentence states no duty, date or figure of its own.
 */
const STRUCTURAL_SUBJECT =
  /\b(?:(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last|next|previous|final|left|right|\d+(?:st|nd|rd|th))\s+(?:row|column|line|bullet|item|step|table|paragraph|section|box)|(?:row|rows|column|columns|checklist|table|memo|bullet|bullets|step|steps|list|chart|box|sidebar|callout)\b)/i;
const STRUCTURAL_RELATION =
  /\b(?:rests?|sits?|sit|appears?|follows?|lies|lie|relies|belongs?|comes?|goes|goes with|maps?|ties?|links?|points?|is based|are based|is drawn|are drawn|is keyed|are keyed|is tied|are tied|is built|are built)\b/i;
const POINTER_TO_THE_LAW =
  /\b(?:is|are)\s+(?:the|a)\s+(?:section|sections|provision|provisions|rule|rules|statute|statutes|place|places|one|ones)\s+(?:to|you|that|for)\s+(?:read|check|look|see|consult|turn|start|use|apply|test)\b|\b(?:is|are)\s+where\s+(?:to|you)\s+(?:look|read|start|check)\b|\bworth\s+(?:reading|checking)\b|\bis\s+the\s+(?:section|provision)\s+(?:to|for)\b/i;
/** An editor's adjective about a provision: it describes the statute, it does not say what the statute says. */
const CHARACTERISATION =
  /\b(?:is|are)\s+(?:very |quite |fairly |particularly |especially |unusually |remarkably |notably |rather )?(?:specific|detailed|explicit|precise|careful|brief|thorough|helpful|useful|relevant|important|central|key|instructive|telling|worth\s+\w+)\s+(?:about|on|regarding|in|for|here|to)\b/i;

export function isPointer(sentence: string): boolean {
  const own = withoutQuotes(sentence);
  if (MODAL_DUTY.test(own) || DATE_OR_PERIOD.test(own) || FIGURE.test(own)) return false;
  return (
    (STRUCTURAL_SUBJECT.test(own) && STRUCTURAL_RELATION.test(own)) || POINTER_TO_THE_LAW.test(own)
  );
}

export function isCharacterisation(sentence: string): boolean {
  const own = withoutQuotes(sentence);
  if (MODAL_DUTY.test(own) || DATE_OR_PERIOD.test(own) || FIGURE.test(own)) return false;
  return CHARACTERISATION.test(own);
}

/**
 * Why a unit of the article is not a claim, or null when it may be one. A table row is judged cell by cell
 * of its own accord elsewhere, so it is never a "fragment" for lacking a verb; its header row is skipped by
 * the extractor, which knows which row that is.
 */
export function whyNotAClaim(
  sentence: string,
  options: { row?: boolean } = {},
): NonClaimReason | null {
  if (isQuestion(sentence)) return 'question';
  if (!options.row && isAdvice(sentence)) return 'advice';
  if (isPointer(sentence)) return 'pointer';
  if (isCharacterisation(sentence)) return 'judgement';
  const plain = body(sentence);
  if (
    !options.row &&
    NAMES_A_PROVISION_OR_FORM.test(sentence) &&
    wordCount(plain) <= FRAGMENT_MAX_WORDS &&
    NOUN_PHRASE_OPENER.test(plain) &&
    !hasFiniteVerb(sentence) &&
    !(DATE_OR_PERIOD.test(sentence) || FIGURE.test(sentence) || MODAL_DUTY.test(sentence))
  )
    return 'fragment';
  return null;
}
